// frontend/src/pages/technician/TechnicianDashboard.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    CircularProgress,
    Paper,
    LinearProgress,
    Divider,
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Container,
    Snackbar
} from '@mui/material';
import {
    Assignment,
    Build,
    Schedule,
    LocationOn,
    Phone,
    Navigation,
    CheckCircle,
    Warning,
    PlayArrow,
    Stop,
    Refresh,
    Camera,
    Inventory,
    Map,
    TrendingUp,
    AccessTime,
    Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTechnicianAuth } from '../../contexts/TechnicianAuthContext';
import { technicianApiService } from '../../services/technicianApiService';

const TechnicianDashboard = () => {
    // Estados principales
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [workSessionActive, setWorkSessionActive] = useState(false);
    const [activeSession, setActiveSession] = useState(null);
    const [locationEnabled, setLocationEnabled] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Estados de UI
    const [workSessionDialog, setWorkSessionDialog] = useState(false);
    const [workSessionLoading, setWorkSessionLoading] = useState(false);
    const [sessionCheckLoading, setSessionCheckLoading] = useState(false);
    const [error, setError] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Hooks
    const navigate = useNavigate();
    const { technician } = useTechnicianAuth();

    // Efecto principal de inicialización
    useEffect(() => {
        let mounted = true;

        const initializeDashboard = async () => {
            if (!mounted) return;

            try {
                setLoading(true);
                console.log('TechnicianDashboard - Inicializando dashboard...');

                // Cargar datos en paralelo
                const promises = [
                    loadDashboardData(),
                    checkWorkSessionStatus(),
                    requestLocationPermission()
                ];

                await Promise.allSettled(promises);

                if (!mounted) return;

                // Configurar interval para actualizaciones periódicas
                const interval = setInterval(() => {
                    if (mounted) {
                        refreshData();
                    }
                }, 120000); // 2 minutos

                // Cleanup function
                return () => {
                    clearInterval(interval);
                };

            } catch (error) {
                console.error('TechnicianDashboard - Error inicializando:', error);
                if (mounted) {
                    setError('Error inicializando el dashboard');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        const cleanup = initializeDashboard();

        return () => {
            mounted = false;
            if (cleanup && typeof cleanup.then === 'function') {
                cleanup.then(cleanupFn => {
                    if (typeof cleanupFn === 'function') {
                        cleanupFn();
                    }
                });
            }
        };
    }, []);

    // [Capa 5 Auditoría] Rastro de Ubicación de Fondo (Breadcrumbs Tracking)
    useEffect(() => {
        let mounted = true;
        let trackerInterval = null;

        const trackLocation = () => {
            if (!navigator.geolocation) return;

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (mounted) {
                        console.log('📍 [Capa 5 Auditoría] Obteniendo ubicación de jornada periódica:', position.coords);
                        updateLocation(position.coords);
                    }
                },
                (err) => {
                    console.warn('⚠️ [Capa 5 Auditoría] Error al obtener ubicación de jornada periódica:', err.message);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        };

        if (workSessionActive) {
            console.log('📡 [Capa 5 Auditoría] Iniciando rastreador GPS de jornada activa...');
            // Enviar ubicación de inmediato
            trackLocation();

            // Reducir el intervalo de 5 minutos (300,000 ms) a 2 minutos (120,000 ms) si hay instalación en progreso
            const isInProgress = dashboardData?.stats?.in_progress_installations > 0;
            const trackingIntervalTime = isInProgress ? 120000 : 300000;
            console.log(`⏱️ Intervalo de tracking GPS: ${trackingIntervalTime / 1000}s (Instalación en progreso: ${isInProgress})`);

            trackerInterval = setInterval(() => {
                trackLocation();
            }, trackingIntervalTime);
        }

        return () => {
            mounted = false;
            if (trackerInterval) {
                clearInterval(trackerInterval);
                console.log('📡 [Capa 5 Auditoría] Rastreador GPS de jornada activa detenido.');
            }
        };
    }, [workSessionActive, dashboardData?.stats?.in_progress_installations]);

    // Función para cargar datos del dashboard
    const loadDashboardData = async () => {
        try {
            console.log('TechnicianDashboard - Cargando datos del dashboard...');

            const data = await technicianApiService.getDashboardSummary();
            console.log('TechnicianDashboard - Datos recibidos:', data);

            setDashboardData(data);
            setError(null);
        } catch (error) {
            console.error('TechnicianDashboard - Error cargando dashboard:', error);

            // Datos de fallback para mostrar algo al usuario
            const fallbackData = {
                technician: technician || { name: 'Técnico', email: '' },
                stats: {
                    pending_installations: 0,
                    completed_installations: 0,
                    in_progress_installations: 0,
                    open_tickets: 0,
                    in_progress_tickets: 0,
                    resolved_tickets: 0
                },
                todayActivity: {
                    installations_today: 0,
                    tickets_today: 0
                },
                inventorySummary: {
                    total_items: 0,
                    total_quantity: 0,
                    total_value: 0
                },
                upcoming_installations: [],
                assigned_tickets: [],
                inventory: []
            };

            setDashboardData(fallbackData);
            setError('Error cargando algunos datos del dashboard. Mostrando información básica.');
        }
    };

    // Función para verificar estado real de la sesión desde el servidor
    const checkWorkSessionStatus = async () => {
        try {
            setSessionCheckLoading(true);
            console.log('TechnicianDashboard - Verificando estado de sesión de trabajo...');

            const response = await technicianApiService.getWorkSessionStatus();

            const hasActiveSession = response.hasActiveSession;
            const session = response.activeSession;

            console.log('TechnicianDashboard - Estado de sesión obtenido:', { hasActiveSession, session });

            setWorkSessionActive(hasActiveSession);
            setActiveSession(session);

            // Sincronizar localStorage con el estado real del servidor
            if (hasActiveSession && session) {
                localStorage.setItem('activeWorkSession', JSON.stringify(session));
                console.log('TechnicianDashboard - Sesión sincronizada en localStorage');
            } else {
                localStorage.removeItem('activeWorkSession');
                console.log('TechnicianDashboard - localStorage limpiado');
            }

        } catch (error) {
            console.warn('TechnicianDashboard - Error verificando estado de sesión:', error);

            // Fallback: verificar localStorage como respaldo
            const localSession = localStorage.getItem('activeWorkSession');
            if (localSession) {
                try {
                    const sessionData = JSON.parse(localSession);
                    setWorkSessionActive(true);
                    setActiveSession(sessionData);
                    console.log('TechnicianDashboard - Usando sesión de localStorage como fallback');
                } catch (parseError) {
                    console.warn('TechnicianDashboard - Error parseando sesión local:', parseError);
                    localStorage.removeItem('activeWorkSession');
                    setWorkSessionActive(false);
                    setActiveSession(null);
                }
            } else {
                setWorkSessionActive(false);
                setActiveSession(null);
            }
        } finally {
            setSessionCheckLoading(false);
        }
    };

    // Función para manejar sesión de trabajo con mejor error handling
    const handleWorkSession = async (action) => {
        try {
            setWorkSessionLoading(true);
            setError(null);

            console.log(`TechnicianDashboard - Intentando ${action} sesión de trabajo...`);
            console.log('TechnicianDashboard - Estado actual:', { workSessionActive, activeSession });

            // Verificar estado actual antes de proceder
            if (action === 'start' && workSessionActive) {
                console.log('TechnicianDashboard - Ya hay una sesión activa, verificando estado real...');
                await checkWorkSessionStatus();

                if (workSessionActive) {
                    showSnackbar('Ya tienes una jornada laboral activa', 'warning');
                    setWorkSessionDialog(false);
                    return;
                }
            }

            if (action === 'end' && !workSessionActive) {
                console.log('TechnicianDashboard - No hay sesión activa para finalizar, verificando estado real...');
                await checkWorkSessionStatus();

                if (!workSessionActive) {
                    showSnackbar('No hay jornada laboral activa para finalizar', 'warning');
                    setWorkSessionDialog(false);
                    return;
                }
            }

            const result = await technicianApiService.manageWorkSession(action);

            console.log('TechnicianDashboard - Resultado de sesión:', result);

            if (action === 'start') {
                const session = result.session;
                localStorage.setItem('activeWorkSession', JSON.stringify(session));
                setWorkSessionActive(true);
                setActiveSession(session);
                showSnackbar('Jornada laboral iniciada exitosamente', 'success');
                console.log('TechnicianDashboard - Sesión iniciada exitosamente');
            } else {
                localStorage.removeItem('activeWorkSession');
                setWorkSessionActive(false);
                setActiveSession(null);
                showSnackbar('Jornada laboral finalizada exitosamente', 'success');
                console.log('TechnicianDashboard - Sesión finalizada exitosamente');
            }

            setWorkSessionDialog(false);

            // Refrescar datos después de cambiar sesión
            setTimeout(() => {
                refreshData();
            }, 1000);

        } catch (error) {
            console.error('TechnicianDashboard - Error en sesión de trabajo:', error);

            // Manejar diferentes tipos de errores con axios
            if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.message || error.message;

                if (errorMessage.includes('jornada laboral activa')) {
                    // Ya hay una sesión activa - sincronizar estado
                    console.log('TechnicianDashboard - Detectada sesión activa existente, sincronizando...');
                    await checkWorkSessionStatus();
                    showSnackbar('Ya tienes una jornada laboral activa. Estado sincronizado.', 'info');
                } else if (errorMessage.includes('No hay jornada laboral activa')) {
                    // No hay sesión activa - limpiar estado local
                    console.log('TechnicianDashboard - No hay sesión activa, limpiando estado local...');
                    localStorage.removeItem('activeWorkSession');
                    setWorkSessionActive(false);
                    setActiveSession(null);
                    showSnackbar('No hay jornada laboral activa para finalizar.', 'warning');
                } else {
                    showSnackbar(`Error: ${errorMessage}`, 'error');
                }
            } else if (error.response?.status === 401) {
                showSnackbar('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', 'error');
                // El interceptor de axios ya maneja la redirección
            } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                showSnackbar('La solicitud tardó demasiado. Intenta nuevamente.', 'error');
            } else if (!error.response) {
                showSnackbar('Error de conexión. Verifica tu internet e intenta nuevamente.', 'error');
            } else {
                const errorMessage = error.response?.data?.message || 'Error desconocido';
                showSnackbar(`Error: ${errorMessage}`, 'error');
            }

            setWorkSessionDialog(false);
        } finally {
            setWorkSessionLoading(false);
        }
    };

    // Función para forzar finalización de sesión
    const handleForceEndSession = async () => {
        try {
            setWorkSessionLoading(true);

            console.log('TechnicianDashboard - Forzando finalización de sesión...');

            // Intentar finalizar normalmente primero
            try {
                await technicianApiService.manageWorkSession('end');
                console.log('TechnicianDashboard - Sesión finalizada normalmente');
            } catch (error) {
                console.warn('TechnicianDashboard - Error finalizando normalmente, limpiando estado local:', error);
            }

            // Limpiar estado local siempre
            localStorage.removeItem('activeWorkSession');
            setWorkSessionActive(false);
            setActiveSession(null);

            showSnackbar('Sesión finalizada. Si persisten problemas, contacta soporte.', 'info');
            setWorkSessionDialog(false);

            // Refrescar datos
            setTimeout(() => {
                refreshData();
            }, 1000);

        } catch (error) {
            console.error('TechnicianDashboard - Error forzando fin de sesión:', error);
            showSnackbar('Error forzando finalización. Contacta soporte técnico.', 'error');
            setWorkSessionDialog(false);
        } finally {
            setWorkSessionLoading(false);
        }
    };

    // Función para refrescar datos con sincronización de sesión
    const refreshData = async () => {
        try {
            setRefreshing(true);

            // Cargar datos del dashboard y verificar sesión en paralelo
            const promises = [
                loadDashboardData(),
                checkWorkSessionStatus()
            ];

            await Promise.allSettled(promises);

        } catch (error) {
            console.error('TechnicianDashboard - Error refrescando datos:', error);
            // No mostrar error en refresh automático para no molestar al usuario
        } finally {
            setRefreshing(false);
        }
    };

    // Función para solicitar permisos de ubicación
    const requestLocationPermission = () => {
        if (!navigator.geolocation) {
            setLocationError('Geolocalización no disponible en este navegador');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocationEnabled(true);
                setLocationError(null);
                updateLocation(position.coords);
            },
            (error) => {
                setLocationEnabled(false);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        setLocationError('Acceso a ubicación denegado');
                        break;
                    case error.POSITION_UNAVAILABLE:
                        setLocationError('Información de ubicación no disponible');
                        break;
                    case error.TIMEOUT:
                        setLocationError('Tiempo de espera agotado');
                        break;
                    default:
                        setLocationError('Error obteniendo ubicación');
                        break;
                }
            },
            {
                timeout: 10000,
                enableHighAccuracy: false,
                maximumAge: 300000
            }
        );
    };

    // Función para actualizar ubicación
    const updateLocation = async (coords) => {
        try {
            await technicianApiService.updateLocation({
                latitude: coords.latitude,
                longitude: coords.longitude,
                accuracy: coords.accuracy
            });
        } catch (error) {
            console.warn('TechnicianDashboard - Error actualizando ubicación:', error);
        }
    };

    // Función para mostrar snackbar
    const showSnackbar = (message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    // Función para cerrar snackbar
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Función para obtener color de estado
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'completed': return 'success';
            case 'in_progress': return 'info';
            case 'open': return 'error';
            default: return 'default';
        }
    };

    // Función para obtener color de prioridad
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'error';
            case 'medium': return 'warning';
            case 'low': return 'info';
            default: return 'default';
        }
    };

    // Función para navegar a instalación
    const navigateToInstallation = (installation) => {
        if (installation.coordinates) {
            const coords = installation.coordinates.replace(/[()]/g, '').split(',');
            if (coords.length === 2) {
                const val1 = parseFloat(coords[0].trim());
                const val2 = parseFloat(coords[1].trim());
                let lat = val1;
                let lng = val2;
                if (Math.abs(val1) > 90 && Math.abs(val2) <= 90) {
                    lat = val2;
                    lng = val1;
                }
                window.open(`https://maps.google.com/maps?q=${lat},${lng}`, '_blank');
            }
        } else if (installation.address) {
            window.open(`https://maps.google.com/maps?q=${encodeURIComponent(installation.address)}`, '_blank');
        }
    };

    // Función para reintentar permisos de ubicación
    const retryLocationPermission = () => {
        setLocationError(null);
        requestLocationPermission();
    };

    // Renderizado del dialog de sesión de trabajo
    const renderWorkSessionDialog = () => (
        <Dialog
            open={workSessionDialog}
            onClose={() => !workSessionLoading && setWorkSessionDialog(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                {workSessionActive ? '⏹️ Finalizar Jornada' : '▶️ Iniciar Jornada'}
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" gutterBottom>
                    {workSessionActive
                        ? '¿Estás seguro de que deseas finalizar tu jornada laboral?'
                        : '¿Estás listo para comenzar tu jornada laboral?'
                    }
                </Typography>

                {activeSession && workSessionActive && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="info.dark">
                            📅 Sesión iniciada: {new Date(activeSession.start_time || activeSession.created_at).toLocaleString('es-PE')}
                        </Typography>
                        {activeSession.hours_worked && (
                            <Typography variant="body2" color="info.dark">
                                ⏱️ Tiempo trabajado: {Math.round(activeSession.hours_worked * 100) / 100} horas
                            </Typography>
                        )}
                    </Box>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {workSessionActive
                        ? 'Se registrará el tiempo trabajado y se guardará tu reporte.'
                        : 'Se iniciará el registro de tiempo y actividades.'
                    }
                </Typography>

                {workSessionLoading && (
                    <Box sx={{ mt: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Procesando...
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Button
                    onClick={() => setWorkSessionDialog(false)}
                    size="large"
                    disabled={workSessionLoading}
                >
                    Cancelar
                </Button>

                {workSessionActive && (
                    <Button
                        onClick={handleForceEndSession}
                        variant="outlined"
                        color="warning"
                        size="large"
                        disabled={workSessionLoading}
                    >
                        Forzar Fin
                    </Button>
                )}

                <Button
                    onClick={() => handleWorkSession(workSessionActive ? 'end' : 'start')}
                    variant="contained"
                    color={workSessionActive ? 'error' : 'success'}
                    size="large"
                    sx={{ minWidth: 120 }}
                    disabled={workSessionLoading}
                >
                    {workSessionActive ? 'Finalizar' : 'Iniciar'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Estados de carga
    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Box textAlign="center">
                        <CircularProgress size={60} thickness={4} />
                        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                            Cargando dashboard...
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.disabled' }}>
                            {sessionCheckLoading && 'Verificando sesión de trabajo...'}
                            {!sessionCheckLoading && 'Obteniendo información del técnico...'}
                        </Typography>
                    </Box>
                </Box>
            </Container>
        );
    }

    if (!dashboardData) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={loadDashboardData}>
                            Reintentar
                        </Button>
                    }
                >
                    Error cargando el dashboard. Por favor, intenta nuevamente.
                </Alert>
            </Container>
        );
    }

    const currentTime = new Date().toLocaleTimeString('es-PE', {
        hour: '2-digit',
        minute: '2-digit'
    });

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Header mejorado */}
            <Paper
                elevation={2}
                sx={{
                    p: 3,
                    mb: 3,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: 'white',
                    borderRadius: 2
                }}
            >
                <Grid container alignItems="center" spacing={3}>
                    <Grid item>
                        <Avatar
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                width: 64,
                                height: 64,
                                fontSize: '1.5rem',
                                border: '2px solid rgba(255,255,255,0.3)'
                            }}
                        >
                            {(dashboardData.technician?.name || technician?.name || 'T').charAt(0)}
                        </Avatar>
                    </Grid>
                    <Grid item xs>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                            ¡Hola, {dashboardData.technician?.name || technician?.name || 'Técnico'}!
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 'normal' }}>
                            {new Date().toLocaleDateString('es-PE', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })} - {currentTime}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box display="flex" flexDirection="column" gap={1} alignItems="end">
                            <Chip
                                icon={<LocationOn />}
                                label={locationEnabled ? 'GPS Activo' : 'GPS Inactivo'}
                                color={locationEnabled ? 'success' : 'warning'}
                                variant="outlined"
                                sx={{
                                    color: 'white',
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    fontWeight: 'bold'
                                }}
                                onClick={!locationEnabled ? retryLocationPermission : undefined}
                                clickable={!locationEnabled}
                            />
                            <Chip
                                icon={workSessionActive ? <Stop /> : <PlayArrow />}
                                label={workSessionActive ? 'En Servicio' : 'Fuera de Servicio'}
                                color={workSessionActive ? 'success' : 'default'}
                                variant="filled"
                                sx={{
                                    bgcolor: workSessionActive ? 'success.main' : 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                                onClick={() => setWorkSessionDialog(true)}
                                clickable
                            />
                        </Box>
                    </Grid>
                </Grid>

                {refreshing && (
                    <LinearProgress
                        sx={{
                            mt: 2,
                            bgcolor: 'rgba(255,255,255,0.3)',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: 'rgba(255,255,255,0.8)'
                            }
                        }}
                    />
                )}
            </Paper>

            {/* Alertas */}
            {error && (
                <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {locationError && (
                <Alert
                    severity="info"
                    sx={{ mb: 3 }}
                    action={
                        <Button color="inherit" size="small" onClick={retryLocationPermission}>
                            Activar GPS
                        </Button>
                    }
                >
                    {locationError}
                </Alert>
            )}

            {/* Estadísticas mejoradas */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Instalaciones Pendientes
                                    </Typography>
                                    <Typography variant="h3" component="div" color="warning.main">
                                        {dashboardData?.stats?.pending_installations || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark' }}>
                                    <Assignment />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Completadas Hoy
                                    </Typography>
                                    <Typography variant="h3" component="div" color="success.main">
                                        {dashboardData?.todayActivity?.installations_today || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark' }}>
                                    <CheckCircle />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Tickets Pendientes
                                    </Typography>
                                    <Typography variant="h3" component="div" color="error.main">
                                        {dashboardData?.stats?.open_tickets || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'error.light', color: 'error.dark' }}>
                                    <Build />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                        Equipos Asignados
                                    </Typography>
                                    <Typography variant="h3" component="div" color="info.main">
                                        {dashboardData?.inventorySummary?.total_items || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'info.light', color: 'info.dark' }}>
                                    <Inventory />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Contenido principal */}
            <Grid container spacing={3}>
                {/* Instalaciones de Hoy */}
                <Grid item xs={12} lg={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6" component="div">
                                    📅 Instalaciones de Hoy
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => navigate('/technician/installations')}
                                    endIcon={<Assignment />}
                                >
                                    Ver Todas
                                </Button>
                            </Box>

                            {dashboardData?.upcoming_installations?.length > 0 ? (
                                <List dense>
                                    {dashboardData.upcoming_installations.slice(0, 3).map((installation, index) => (
                                        <React.Fragment key={installation.id}>
                                            <ListItem
                                                sx={{
                                                    px: 0,
                                                    '&:hover': { bgcolor: 'action.hover', borderRadius: 1 }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                        <Person />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={`${installation.first_name} ${installation.last_name}`}
                                                    secondary={
                                                        <Box component="span">
                                                            <Box component="span" display="block">
                                                                📍 {installation.address}
                                                            </Box>
                                                            <Box component="span" display="block" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                                                                📶 {installation.plan_name} - S/. {installation.plan_price}
                                                            </Box>
                                                        </Box>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <Box display="flex" gap={0.5}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => window.open(`tel:${installation.phone}`)}
                                                            title="Llamar"
                                                        >
                                                            <Phone />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => navigateToInstallation(installation)}
                                                            title="Navegar"
                                                        >
                                                            <Navigation />
                                                        </IconButton>
                                                    </Box>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {index < Math.min(dashboardData.upcoming_installations.length - 1, 2) && (
                                                <Divider component="li" />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    🎉 No hay instalaciones programadas para hoy
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Tickets Asignados */}
                <Grid item xs={12} lg={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6" component="div">
                                    🎫 Tickets Asignados
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => navigate('/technician/tickets')}
                                    endIcon={<Build />}
                                >
                                    Ver Todos
                                </Button>
                            </Box>

                            {dashboardData?.assigned_tickets?.length > 0 ? (
                                <List dense>
                                    {dashboardData.assigned_tickets.slice(0, 3).map((ticket, index) => (
                                        <React.Fragment key={ticket.id}>
                                            <ListItem
                                                sx={{
                                                    px: 0,
                                                    '&:hover': { bgcolor: 'action.hover', borderRadius: 1 }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: `${getPriorityColor(ticket.priority)}.main` }}>
                                                        <Warning />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={ticket.subject}
                                                    secondary={
                                                        <Box component="span">
                                                            <Box component="span" display="block">
                                                                👤 {ticket.first_name} {ticket.last_name}
                                                            </Box>
                                                            <Box component="span" display="block" sx={{ mt: 0.5 }}>
                                                                <Chip
                                                                    label={ticket.priority.toUpperCase()}
                                                                    size="small"
                                                                    color={getPriorityColor(ticket.priority)}
                                                                    variant="outlined"
                                                                />
                                                            </Box>
                                                        </Box>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => navigate(`/technician/tickets/${ticket.id}`)}
                                                        title="Ver ticket"
                                                    >
                                                        <Build />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {index < Math.min(dashboardData.assigned_tickets.length - 1, 2) && (
                                                <Divider component="li" />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    ✅ No hay tickets pendientes
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Mi Inventario */}
                <Grid item xs={12}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h6" component="div">
                                    📦 Mi Inventario Asignado
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => navigate('/technician/inventory')}
                                    endIcon={<Inventory />}
                                >
                                    Ver Completo
                                </Button>
                            </Box>

                            {dashboardData?.inventory?.length > 0 ? (
                                <Grid container spacing={2}>
                                    {dashboardData.inventory.slice(0, 6).map((item) => (
                                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                                            <Card variant="outlined" sx={{ height: '100%' }}>
                                                <CardContent sx={{ p: 2 }}>
                                                    <Typography variant="subtitle1" gutterBottom>
                                                        {item.item_name}
                                                    </Typography>
                                                    <Typography color="textSecondary" variant="body2" gutterBottom>
                                                        {item.brand} {item.model}
                                                    </Typography>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                                        <Chip
                                                            label={`Cantidad: ${item.quantity}`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                        <Typography variant="caption" color="textSecondary">
                                                            {item.category_name}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Alert severity="info">
                                    📝 No tienes equipos asignados actualmente
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* FAB para refrescar */}
            <Fab
                color="primary"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000
                }}
                onClick={refreshData}
                disabled={refreshing}
            >
                <Refresh />
            </Fab>

            {/* Dialog mejorado para gestionar sesión de trabajo */}
            {renderWorkSessionDialog()}

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default TechnicianDashboard;